import React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * ConfirmModal - A confirmation dialog built on Radix AlertDialog so it
 * correctly stacks on top of other Radix dialogs (which block pointer events
 * on everything outside their own focus scope via RemoveScroll).
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  // The cancel button's own action (the default: just closing, as Escape and a click
  // outside do).
  onCancel,
  danger = false,
}) => {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.confirm');

  return (
    <AlertDialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <AlertDialogPrimitive.Content
          className="fixed z-[200] w-full bg-card text-foreground border-border shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 left-0 right-0 bottom-0 rounded-t-2xl border-t md:left-1/2 md:top-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-md md:rounded-xl md:border md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            {danger && (
              <div className="flex-shrink-0 w-8 h-8 rounded-md bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-destructive" />
              </div>
            )}
            <AlertDialogPrimitive.Title className="text-sm font-semibold text-foreground">
              {title}
            </AlertDialogPrimitive.Title>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
              {message}
            </AlertDialogPrimitive.Description>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end max-md:justify-between gap-2 px-5 py-3 border-t border-border bg-muted/20">
            <AlertDialogPrimitive.Cancel
              onClick={onCancel || onClose}
              className="inline-flex h-8 items-center justify-center rounded-md border border-input bg-transparent px-3 text-xs font-medium text-foreground shadow-sm hover:bg-accent transition-colors"
            >
              {cancelLabel ?? t('common.cancel')}
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action
              onClick={onConfirm}
              className={`inline-flex h-8 items-center justify-center rounded-md px-3 text-xs font-medium shadow transition-colors ${
                danger
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : 'bg-amber-500 text-black hover:bg-amber-400'
              }`}
            >
              {resolvedConfirmLabel}
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
};

export default ConfirmModal;
